import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Trophy,
  Medal,
  Crown,
  Award,
  Users,
  Calendar,
  ArrowLeft,
  Target,
} from "lucide-react";
import { formatDateInTimezone, type TimezoneOption } from "@/utils/timezone";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TournamentResult {
  tournament_id: string;
  tournament_name: string;
  description: string;
  tournament_type: string;
  status: string;
  start_time: string;
  end_time: string;
  level: number;
  difficulty: string;
  prize_pool_xp: number;
  max_participants: number;
  total_participants: number;
  winning_score: number;
  average_score: number;
  lowest_score: number;
  first_place_score: number;
  second_place_score: number;
  third_place_score: number;
}

interface Ranking {
  tournament_id: string;
  user_id: string;
  rank: number;
  score: number;
  username: string;
  avatar: string;
  prize_xp: number;
}

export default function TournamentResults() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState<TournamentResult | null>(null);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimezone, setSelectedTimezone] =
    useState<TimezoneOption>("CST");

  useEffect(() => {
    if (tournamentId) {
      fetchResults();
    }
  }, [tournamentId]);

  const fetchResults = async () => {
    setLoading(true);

    // Fetch tournament summary
    const { data: tournamentData, error: tournamentError } = await supabase
      .from("tournament_results")
      .select("*")
      .eq("tournament_id", tournamentId)
      .single();

    if (tournamentError) {
      console.error("Error fetching tournament:", tournamentError);
    } else {
      setTournament(tournamentData);
    }

    // Fetch rankings
    const { data: rankingsData, error: rankingsError } = await supabase
      .from("tournament_rankings")
      .select("*")
      .eq("tournament_id", tournamentId)
      .order("rank", { ascending: true });

    if (rankingsError) {
      console.error("Error fetching rankings:", rankingsError);
    } else {
      setRankings(rankingsData || []);
    }

    setLoading(false);
  };

  const formatDate = (dateString: string) => {
    return formatDateInTimezone(dateString, selectedTimezone);
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-8 h-8 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-8 h-8 text-gray-400" />;
    if (rank === 3) return <Medal className="w-8 h-8 text-amber-600" />;
    return null;
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1)
      return "bg-gradient-to-r from-yellow-400 to-yellow-600 text-white";
    if (rank === 2)
      return "bg-gradient-to-r from-gray-300 to-gray-500 text-white";
    if (rank === 3)
      return "bg-gradient-to-r from-amber-500 to-amber-700 text-white";
    return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300";
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Tournament results not found.</p>
              <Button onClick={() => navigate("/competitive")} className="mt-4">
                Back to Tournaments
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const topThree = rankings.slice(0, 3);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/competitive")}
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Tournaments
      </Button>

      {/* Tournament Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-8 h-8 text-yellow-600" />
          <h1 className="text-3xl font-bold">{tournament.tournament_name}</h1>
        </div>
        {tournament.description && (
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {tournament.description}
          </p>
        )}

        {/* Timezone Selector */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Time Zone:
          </span>
          <Select
            value={selectedTimezone}
            onValueChange={(value) =>
              setSelectedTimezone(value as TimezoneOption)
            }
          >
            <SelectTrigger className="w-[160px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CST">CST (UTC-6)</SelectItem>
              <SelectItem value="EST">EST (UTC-5)</SelectItem>
              <SelectItem value="PST">PST (UTC-8)</SelectItem>
              <SelectItem value="local">Local Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tournament Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Calendar className="w-5 h-5 mx-auto mb-1 text-gray-600" />
            <div className="text-xs text-gray-500">Ended</div>
            <div className="text-sm font-medium">
              {formatDate(tournament.end_time)}
            </div>
          </div>

          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Users className="w-5 h-5 mx-auto mb-1 text-gray-600" />
            <div className="text-xs text-gray-500">Participants</div>
            <div className="text-sm font-medium">
              {tournament.total_participants}
            </div>
          </div>

          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Target className="w-5 h-5 mx-auto mb-1 text-gray-600" />
            <div className="text-xs text-gray-500">Level</div>
            <div className="text-sm font-medium">Level {tournament.level}</div>
          </div>

          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Award className="w-5 h-5 mx-auto mb-1 text-yellow-600" />
            <div className="text-xs text-gray-500">Prize Pool</div>
            <div className="text-sm font-medium">
              {tournament.prize_pool_xp} XP
            </div>
          </div>
        </div>
      </div>

      {/* Winner Podium */}
      {topThree.length > 0 && (
        <Card className="mb-8 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-gray-800 dark:to-gray-900">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-600" />
              Winners
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 2nd Place */}
              {topThree[1] && (
                <div className="order-1 md:order-1">
                  <div
                    className={`p-6 rounded-lg ${getRankBadge(2)} text-center`}
                  >
                    <div className="flex justify-center mb-2">
                      {getRankIcon(2)}
                    </div>
                    <div className="text-2xl font-bold mb-1">2nd Place</div>
                    <div className="text-lg font-semibold mb-2">
                      {topThree[1].username}
                    </div>
                    <div className="text-3xl font-bold mb-2">
                      {topThree[1].score}
                    </div>
                    <div className="text-sm opacity-90">
                      <Award className="w-4 h-4 inline mr-1" />
                      {topThree[1].prize_xp} XP
                    </div>
                  </div>
                </div>
              )}

              {/* 1st Place (Center, Larger) */}
              {topThree[0] && (
                <div className="order-0 md:order-2">
                  <div
                    className={`p-8 rounded-lg ${getRankBadge(1)} text-center transform md:scale-110`}
                  >
                    <div className="flex justify-center mb-3">
                      {getRankIcon(1)}
                    </div>
                    <div className="text-3xl font-bold mb-2">Champion</div>
                    <div className="text-xl font-semibold mb-3">
                      {topThree[0].username}
                    </div>
                    <div className="text-4xl font-bold mb-3">
                      {topThree[0].score}
                    </div>
                    <div className="text-base font-semibold">
                      <Award className="w-5 h-5 inline mr-1" />
                      {topThree[0].prize_xp} XP
                    </div>
                  </div>
                </div>
              )}

              {/* 3rd Place */}
              {topThree[2] && (
                <div className="order-2 md:order-3">
                  <div
                    className={`p-6 rounded-lg ${getRankBadge(3)} text-center`}
                  >
                    <div className="flex justify-center mb-2">
                      {getRankIcon(3)}
                    </div>
                    <div className="text-2xl font-bold mb-1">3rd Place</div>
                    <div className="text-lg font-semibold mb-2">
                      {topThree[2].username}
                    </div>
                    <div className="text-3xl font-bold mb-2">
                      {topThree[2].score}
                    </div>
                    <div className="text-sm opacity-90">
                      <Award className="w-4 h-4 inline mr-1" />
                      {topThree[2].prize_xp} XP
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Rankings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Final Rankings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {rankings.map((ranking, index) => (
              <div
                key={ranking.user_id}
                className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                  index < 3
                    ? "bg-yellow-50 dark:bg-yellow-900/10 border-2 border-yellow-200 dark:border-yellow-800"
                    : "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <div className="flex items-center gap-4 flex-grow">
                  <div className="flex items-center justify-center w-12">
                    {getRankIcon(ranking.rank) || (
                      <span className="text-xl font-bold text-gray-600">
                        #{ranking.rank}
                      </span>
                    )}
                  </div>

                  <div className="flex-grow">
                    <div className="font-semibold text-lg">
                      {ranking.username}
                    </div>
                    <div className="text-sm text-gray-500">
                      @{ranking.username}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-2xl font-bold">{ranking.score}</div>
                    <div className="text-xs text-gray-500">points</div>
                  </div>

                  {ranking.prize_xp > 0 && (
                    <div className="text-right bg-yellow-100 dark:bg-yellow-900/30 px-3 py-2 rounded-lg">
                      <div className="text-lg font-bold text-yellow-700 dark:text-yellow-400">
                        {ranking.prize_xp} XP
                      </div>
                      <div className="text-xs text-yellow-600 dark:text-yellow-500">
                        prize
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Statistics Summary */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold mb-4">Tournament Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-100 dark:bg-gray-700 rounded">
                <div className="text-2xl font-bold text-blue-600">
                  {tournament.winning_score}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Highest Score
                </div>
              </div>

              <div className="text-center p-3 bg-gray-100 dark:bg-gray-700 rounded">
                <div className="text-2xl font-bold text-green-600">
                  {Math.round(tournament.average_score)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Average Score
                </div>
              </div>

              <div className="text-center p-3 bg-gray-100 dark:bg-gray-700 rounded">
                <div className="text-2xl font-bold text-purple-600">
                  {tournament.lowest_score || 0}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Lowest Score
                </div>
              </div>

              <div className="text-center p-3 bg-gray-100 dark:bg-gray-700 rounded">
                <div className="text-2xl font-bold text-orange-600">
                  {tournament.total_participants}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Total Players
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
